/**
 * Extracts the story content from a capture's note_body_md.
 * Iron rule #20: captures have exactly 4 H2 sections — Audit Trail / Capture File / Summary / Note.
 * Mobile renders ## Note only; we do the same for the story reader.
 */

export interface StoryContent {
  title: string;           // from **Bold Title** in Work Body
  subtitle: string;        // usually "(English Translation)" part
  body: string;            // main story paragraphs (plain text)
  culturalBg: string;      // ### Cultural Backgrounder section
  ageRating: string;       // from ### Audience & Age Rating
  motif: string;           // ATU motif
  cultureLineage: string;  // culture-lineage value
}

/** Extract the content of a named H2 section (stops at next H2 or end). */
function extractH2Section(md: string, heading: string): string {
  const re = new RegExp(`## ${heading}\\s*([\\s\\S]*?)(?=\\n## [A-Z]|$)`, 'i');
  const m = md.match(re);
  return m ? m[1].trim() : '';
}

/** Extract the content of a named H3 section within a parent section text. */
function extractH3Section(sectionText: string, heading: string): string {
  const re = new RegExp(`### ${heading}\\s*([\\s\\S]*?)(?=\\n### |$)`, 'i');
  const m = sectionText.match(re);
  return m ? m[1].trim() : '';
}

/** Get a single front-matter style value: "- **key**: value" */
function extractBulletValue(text: string, key: string): string {
  const re = new RegExp(`\\*\\*${key}\\*\\*:\\s*(.+)`, 'i');
  const m = text.match(re);
  return m ? m[1].trim() : '';
}

export function parseStoryContent(noteBodyMd: string): StoryContent {
  const noteSection = extractH2Section(noteBodyMd, 'Note');
  const workBody = extractH3Section(noteSection, 'Work Body') || noteSection;
  const culturalBg = extractH3Section(noteSection, 'Cultural Backgrounder');
  const audienceSection = extractH3Section(noteSection, 'Audience & Age Rating');
  const lineageSection = extractH3Section(noteSection, 'Cultural Lineage & Motif');

  // Title: first **Bold** line, possibly "(Subtitle)"
  const titleMatch = workBody.match(/^\*\*([^*]+)\*\*/);
  const rawTitle = titleMatch ? titleMatch[1].trim() : '';

  // Split title: "Chinese Title (English Subtitle)" or "Title"
  let title = rawTitle;
  let subtitle = '';
  const parenMatch = rawTitle.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (parenMatch) {
    title = parenMatch[1].trim();
    subtitle = parenMatch[2].trim();
  }

  // Body: everything after the title line, strip metadata sections
  const bodyRaw = titleMatch
    ? workBody.slice(workBody.indexOf(titleMatch[0]) + titleMatch[0].length).trim()
    : workBody;

  const ageRating = extractBulletValue(audienceSection, 'age-rating');
  const motif = extractBulletValue(lineageSection, 'folk-motif');
  const cultureLineage = extractBulletValue(lineageSection, 'culture-lineage');

  return {
    title,
    subtitle,
    body: bodyRaw,
    culturalBg,
    ageRating,
    motif,
    cultureLineage,
  };
}

/** Convert a subset of CommonMark / GFM to safe HTML for the story reader.
 *
 * Supports (in this order — block first, then inline):
 *   - ATX headings    `# … ######`          → <h1>…<h6>
 *   - Thematic break  `---` / `***` / `___` (≥3, allow inner spaces)
 *   - Blockquote      `> text` (multi-line, blank line ends)
 *   - Unordered list  `- item` (no nesting)
 *   - Bold            **text**
 *   - Italic          *text*
 *   - Bold+italic     ***text***
 *   - Inline code     `code`
 *   - Link            [label](url)
 *   - Hard break      end-of-line (paragraph: \n → <br />)
 *
 * Out of scope: tables, ordered lists, nested lists, code fences, images,
 * setext headings, footnotes. We deliberately stay simple — LLM crafter
 * output is the only input shape we need to render.
 */
function escapeInline(s: string): string {
  // Best-effort: escape stray `<` that aren't already part of an HTML tag we
  // emitted ourselves. Cheap and safe enough for LLM-produced prose; we do
  // NOT touch `>` so existing blockquote markers survive earlier pre-pass.
  return s.replace(/<(?![a-zA-Z/!])/g, '&lt;');
}

function applyInline(s: string): string {
  return s
    // Bold+italic (must run before bold/italic alone)
    .replace(/\*\*\*([^*\n]+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    // Bold
    .replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>')
    // Italic — non-greedy, disallow internal `*`/newline so it can't span hr lines
    .replace(/(^|[^\w*])\*([^*\n]+?)\*(?!\w)/g, '$1<em>$2</em>')
    // Inline code
    .replace(/`([^`\n]+?)`/g, '<code>$1</code>')
    // Links — open in new tab, harden against tabnabbing
    .replace(
      /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
}

/** True when the trimmed line is a thematic break (---, ***, ___, with or
 *  without internal whitespace). */
function isThematicBreak(line: string): boolean {
  const t = line.trim();
  if (t.length < 3) return false;
  // Strip whitespace, then check that all remaining chars are the same marker
  const compact = t.replace(/\s+/g, '');
  return (
    compact.length >= 3 &&
    (/^-{3,}$/.test(compact) || /^\*{3,}$/.test(compact) || /^_{3,}$/.test(compact))
  );
}

export function mdToHtml(md: string): string {
  if (!md) return '';

  // ── Block-level pre-pass (line-oriented) ─────────────────────────────────
  // Walk the source line by line and emit one HTML "block" per logical unit:
  //   heading | hr | blockquote group | list item | plain line
  // Plain lines stay raw — the paragraph-wrap pass below batches them on
  // blank-line boundaries.
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let bqBuf: string[] = [];

  const flushBlockquote = (): void => {
    if (bqBuf.length === 0) return;
    // Render inner content as its own paragraph(s) inside the blockquote so
    // multi-paragraph quotes work; for the single-paragraph common case this
    // simply emits one <p>.
    const inner = bqBuf.join('\n');
    const innerHtml = innerBlockquoteHtml(inner);
    out.push(`<blockquote>${innerHtml}</blockquote>`);
    bqBuf = [];
  };

  for (const raw of lines) {
    // Blockquote: optional leading whitespace + `>` + optional one space
    const bq = raw.match(/^\s*>\s?(.*)$/);
    if (bq) {
      bqBuf.push(bq[1]);
      continue;
    }
    // Anything else ends the current blockquote group
    if (bqBuf.length > 0) flushBlockquote();

    if (isThematicBreak(raw)) {
      out.push('<hr />');
      continue;
    }
    // ATX heading: 1-6 leading '#', optional trailing '#' tail
    const h = raw.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (h) {
      const lv = h[1].length;
      out.push(`<h${lv}>${applyInline(escapeInline(h[2]))}</h${lv}>`);
      continue;
    }
    // List item: -, *, or + as bullet marker
    const li = raw.match(/^\s*[-*+]\s+(.+)$/);
    if (li) {
      out.push(`<li>${applyInline(escapeInline(li[1]))}</li>`);
      continue;
    }
    // Plain line — defer paragraph wrapping until we batch by blank lines
    out.push(raw);
  }
  flushBlockquote();

  // Wrap consecutive <li> in <ul>
  let html = out.join('\n').replace(
    /(?:^|\n)((?:<li>[\s\S]*?<\/li>(?:\n<li>[\s\S]*?<\/li>)*))/g,
    (_m, block: string) => `\n<ul>${block}</ul>`
  );

  // ── Paragraph-wrap pass ──────────────────────────────────────────────────
  // Split on blank lines. Each chunk is either already a block (heading, hr,
  // ul, blockquote, …) or a run of prose lines we wrap into a single <p>.
  const BLOCK_RE = /^<(h[1-6]|ul|ol|li|hr|p|blockquote|pre|table|div)\b/i;
  html = html
    .split(/\n{2,}/)
    .map((para) => {
      const t = para.trim();
      if (!t) return '';
      if (BLOCK_RE.test(t)) return t;
      // Prose: inline-format, then join inner newlines with <br />
      return `<p>${applyInline(escapeInline(t)).replace(/\n/g, '<br />')}</p>`;
    })
    .filter(Boolean)
    .join('\n');

  return html;
}

/** Render the inner of a `<blockquote>` — supports nested paragraphs &
 *  inline marks, but no further block grammar (no nested headings/lists). */
function innerBlockquoteHtml(inner: string): string {
  return inner
    .split(/\n{2,}/)
    .map((para) => {
      const t = para.trim();
      if (!t) return '';
      return `<p>${applyInline(escapeInline(t)).replace(/\n/g, '<br />')}</p>`;
    })
    .filter(Boolean)
    .join('');
}

/** Format a Unix ms timestamp as a human-friendly date. */
export function formatDate(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Get a brief 1–2 sentence preview (≤120 chars) from summary text. */
export function previewText(summary: string, maxLen = 120): string {
  if (!summary) return '';
  const trimmed = summary.trim();
  if (trimmed.length <= maxLen) return trimmed;
  const cut = trimmed.lastIndexOf('，', maxLen) || trimmed.lastIndexOf('。', maxLen) || trimmed.lastIndexOf(' ', maxLen);
  return cut > 40 ? trimmed.slice(0, cut + 1) + '…' : trimmed.slice(0, maxLen) + '…';
}
